<div class="sidenav-content">
  <div class="subscription-wrapper">
    <div class="main-content">

      <!-- Header -->
      <div class="subscription-header">
        <a class="back-link" routerLink="/dashboard" *ngIf="!planGate.blocked">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back
        </a>
        <h1>Subscription</h1>
      </div>

      <!-- Hard paywall. The allowance is gone, so this page is the only one
           the user can reach until the plan is active again. -->
      <div *ngIf="planGate.blocked" class="checkout-banner banner-locked">
        <svg class="lock-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <rect x="3" y="11" width="18" height="11" rx="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
        <div>
          <strong>{{ planGate.summary?.tier === 'free' ? 'Your Free plan has run out' : 'Your monthly limit is reached' }}</strong>
          <span>
            {{ planGate.reason || 'Upgrade to keep capturing and recalling memories.' }}
            The rest of the app stays locked until your plan is active again.
          </span>
        </div>
        <button class="banner-signout" (click)="signOut()">Sign out</button>
      </div>

      <!-- Checkout return banners -->
      <div *ngIf="checkoutState === 'processing'" class="checkout-banner banner-info">
        <span class="spinner"></span>
        <div>
          <strong>Confirming your payment</strong>
          <span>This usually takes a few seconds.</span>
        </div>
      </div>

      <div *ngIf="checkoutState === 'success'" class="checkout-banner banner-success">
        <div>
          <strong>You're all set</strong>
          <span>Your {{ displayName(plan) }} plan is active.</span>
        </div>
        <button class="banner-dismiss" (click)="dismissBanner()" aria-label="Dismiss">&times;</button>
      </div>

      <div *ngIf="checkoutState === 'canceled'" class="checkout-banner banner-muted">
        <div>
          <strong>Checkout cancelled</strong>
          <span>No payment was taken. You can upgrade whenever you're ready.</span>
        </div>
        <button class="banner-dismiss" (click)="dismissBanner()" aria-label="Dismiss">&times;</button>
      </div>

      <div *ngIf="checkoutState === 'failed'" class="checkout-banner banner-error">
        <div>
          <strong>Payment didn't go through</strong>
          <span>Nothing was charged. Please try again or contact support.</span>
        </div>
        <button class="banner-dismiss" (click)="dismissBanner()" aria-label="Dismiss">&times;</button>
      </div>

      <div *ngIf="checkoutState === 'slow'" class="checkout-banner banner-info">
        <div>
          <strong>Still processing</strong>
          <span>Your payment is taking longer than usual. We'll email you once it's confirmed.</span>
        </div>
        <button class="banner-dismiss" (click)="dismissBanner()" aria-label="Dismiss">&times;</button>
      </div>

      <!-- Plan Selection -->
      <div class="plan-container">
        <div class="plan-header">
          <h2 *ngIf="!isSubscribed">Choose the plan that's right for you</h2>
          <h2 *ngIf="isSubscribed">Your subscription</h2>
          <p *ngIf="!isSubscribed">Unlock more memory and supercharge your AI experience</p>
          <p *ngIf="isSubscribed">You're on the {{ displayName(plan) }} plan</p>
        </div>

        <!-- Paid plan card -->
        <div class="plan-card" *ngIf="paidTier" [class.plan-card--current]="isOnPaidTier">

          <div class="plan-badge" *ngIf="isOnPaidTier">Current plan</div>
          <div class="plan-badge plan-badge--muted" *ngIf="!isOnPaidTier">Most popular</div>

          <div class="plan-card-header">
            <div class="plan-icon">
              <svg viewBox="0 0 80 80" fill="none">
                <rect width="80" height="80" rx="18" fill="#f97316" />
                <path d="M25 18V62H55" stroke="white" stroke-width="6" stroke-linecap="round" stroke-linejoin="round" />
                <circle cx="25" cy="18" r="5" fill="white" />
                <circle cx="55" cy="62" r="5" fill="white" />
                <circle cx="40" cy="62" r="3" fill="white" opacity="0.6" />
                <circle cx="25" cy="40" r="3" fill="white" opacity="0.6" />
              </svg>
            </div>
            <div class="plan-title-section">
              <h3>{{ displayName(paidTier.name) }}</h3>
              <div class="plan-price" *ngIf="priceMajor">
                <span class="currency">{{ paidTier.currency === 'USD' ? '$' : paidTier.currency }}</span>
                <span class="amount">{{ priceMajor }}</span>
                <span class="period">/{{ paidTier.interval }}</span>
              </div>
              <div class="plan-price" *ngIf="!priceMajor">
                <span class="period">Pricing unavailable</span>
              </div>
            </div>
          </div>

          <p class="plan-tagline">Store more, remember more, do more</p>

          <div class="plan-features">
            <ul>
              <li>
                <span class="check-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </span>
                <div>
                  <strong>{{ paidTier.monthly_token_limit | number }} memories every month</strong>
                  <span>Store all your important information, conversations, and ideas. Perfect for power users who chat
                    frequently.</span>
                </div>
              </li>
              <li>
                <span class="check-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </span>
                <div>
                  <strong>Rolling monthly storage</strong>
                  <span>Your allocation refreshes every billing cycle. Your memory bank keeps growing with you.</span>
                </div>
              </li>
              <li>
                <span class="check-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </span>
                <div>
                  <strong>Never hit limits</strong>
                  <span>No more "storage full" messages interrupting your flow. Chat as much as you want.</span>
                </div>
              </li>
              <li>
                <span class="check-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </span>
                <div>
                  <strong>Richer conversations</strong>
                  <span>LIAM remembers more context, giving you better, more personalized responses every time.</span>
                </div>
              </li>
            </ul>
          </div>

          <!-- Live usage, only meaningful once subscribed -->
          <div class="plan-usage" *ngIf="isOnPaidTier && usage">
            <div class="usage-row">
              <span class="usage-label">Used this period</span>
              <span class="usage-value">
                {{ usage.tokens_used | number }} / {{ usage.total_tokens | number }}
              </span>
            </div>
            <div class="usage-bar">
              <div class="usage-bar-fill"
                [style.width.%]="usage.total_tokens ? (usage.tokens_used / usage.total_tokens) * 100 : 0"></div>
            </div>
          </div>

          <div class="plan-note" *ngIf="!isSubscribed">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 16v-4M12 8h.01" />
            </svg>
            <span>Perfect if you use LIAM daily for work, projects, or personal tasks.</span>
          </div>

          <!-- Actions -->
          <div class="plan-actions">
            <button mat-raised-button color="primary" class="btn-upgrade" *ngIf="!isOnPaidTier" [disabled]="!canUpgrade"
              (click)="selectPlan()">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path
                  d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
              {{ upgradeLabel }}
            </button>

            <div class="btn-current" *ngIf="isOnPaidTier">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              Current plan
            </div>

            <button mat-raised-button *ngIf="isOnPaidTier" (click)="openUnsubscribeDialog()" class="btn-cancel">
              Cancel subscription
            </button>
          </div>
        </div>

        <!-- No purchasable plan configured -->
        <div class="plan-card" *ngIf="!loading && !paidTier">
          <p class="plan-tagline">No upgrade is available on this workspace right now.</p>
        </div>

        <!-- Free plan comparison -->
        <div class="free-comparison" [class.free-comparison--current]="!isSubscribed">
          <div class="comparison-header">
            <span class="comparison-badge" *ngIf="!isSubscribed">Current plan</span>
            <span class="comparison-badge comparison-badge--muted" *ngIf="isSubscribed">Free plan</span>
            <h4>Free</h4>
          </div>
          <div class="comparison-features">
            <div class="comparison-item">
              <span class="label">Monthly memories</span>
              <span class="value">{{ freeMonthlyLimit | number }}</span>
            </div>
            <div class="comparison-item">
              <span class="label">AI Connectors</span>
              <span class="value">ChatGPT, Claude, Perplexity</span>
            </div>
            <div class="comparison-item">
              <span class="label">Image uploads</span>
              <span class="value">Limited</span>
            </div>
          </div>
        </div>

      </div>

    </div>
  </div>
</div>